/**
 * ZH Stack Reconciler
 * ─────────────────────────────────────────────────────────────
 * Watches the cart for changes. When 3+ stackable items are present,
 * applies the ZHSTACK10 discount code (or ZHSTACKSUB10 if all stackable
 * items are on subscription). When cart drops below threshold, removes
 * the stack discount. Loads site-wide on every page.
 *
 * Reads stackable product IDs from a JSON script tag rendered by Liquid:
 *   <script id="zh-stackable-products" type="application/json">[1234, 5678, ...]</script>
 *
 * Architecture notes:
 *   - Uses FoxTheme's proven FormData POST pattern to /cart/update.js
 *   - Preserves user-applied discount codes (only our two codes are managed)
 *   - Debounced to avoid thrashing during rapid cart changes
 *   - Self-initializing — no explicit bootstrap needed
 *
 * Console debug: search for [zh-stack-reconciler] in DevTools
 */
(function () {
  'use strict';

  // ──────── Configuration ────────
  var STACK_THRESHOLD = 3;
  var STACK_CODE = 'ZHSTACK10';
  var STACK_SUB_CODE = 'ZHSTACKSUB10';
  var DEBOUNCE_MS = 300;
  var DEBUG = false; // set to true to see reconciler logs

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[zh-stack-reconciler]');
    console.log.apply(console, args);
  }

  // ──────── Read stackable IDs from DOM ────────
  function getStackableIds() {
    var el = document.getElementById('zh-stackable-products');
    if (!el) {
      log('No zh-stackable-products script tag found');
      return [];
    }
    try {
      var ids = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(ids)) return [];
      return ids.map(function (id) { return Number(id); }).filter(Boolean);
    } catch (err) {
      console.warn('[zh-stack-reconciler] Failed to parse stackable IDs', err);
      return [];
    }
  }

  // ──────── Cart fetch ────────
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js failed: ' + r.status);
      return r.json();
    });
  }

  // ──────── Discount management ────────
  function getAppliedDiscounts() {
    var discounts = [];
    var listEl = document.querySelector('.cart__discounts');
    if (listEl) {
      listEl.querySelectorAll('.discount').forEach(function (el) {
        var code = el.getAttribute('data-discount-code');
        if (code) discounts.push(code);
      });
    }
    return discounts;
  }

  // Apply a discount via FoxTheme's FormData pattern. Preserves other codes.
  function applyDiscount(code) {
    var current = getAppliedDiscounts();
    if (current.indexOf(code) !== -1) {
      log('Discount already applied:', code);
      return Promise.resolve();
    }

    // Also strip the OTHER stack code if present (they're mutually exclusive)
    var otherCode = code === STACK_CODE ? STACK_SUB_CODE : STACK_CODE;
    current = current.filter(function (c) { return c !== otherCode; });
    current.push(code);

    log('Applying discount:', code, '— full list becomes:', current);

    var formData = new FormData();
    formData.append('discount', current.join(','));

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: formData
    })
      .then(function (r) {
        if (!r.ok) throw new Error('discount apply failed: ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        console.warn('[zh-stack-reconciler] applyDiscount failed', err);
      });
  }

  // Remove BOTH stack codes if present, leaving other user-applied codes alone.
  function removeStackDiscounts() {
    var current = getAppliedDiscounts();
    var filtered = current.filter(function (c) {
      return c !== STACK_CODE && c !== STACK_SUB_CODE;
    });
    if (filtered.length === current.length) {
      log('No stack discounts to remove');
      return Promise.resolve();
    }
    log('Removing stack discounts — full list becomes:', filtered);

    var formData = new FormData();
    formData.append('discount', filtered.join(','));

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: formData
    })
      .then(function (r) {
        if (!r.ok) throw new Error('discount remove failed: ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        console.warn('[zh-stack-reconciler] removeStackDiscounts failed', err);
      });
  }

  // ──────── Reconcile logic ────────
  function reconcile(cart) {
    var stackableIds = getStackableIds();
    if (!stackableIds.length) {
      log('No stackable IDs available — skipping reconcile');
      return Promise.resolve();
    }

    if (!cart || !cart.items) return Promise.resolve();

    // Count stackable items and check subscription status
    var stackableItems = cart.items.filter(function (item) {
      return stackableIds.indexOf(Number(item.product_id)) !== -1;
    });

    var stackableQty = stackableItems.reduce(function (sum, item) {
      return sum + (item.quantity || 0);
    }, 0);

    // Is every stackable item on a subscription (via selling_plan)?
    var allSubscription =
      stackableItems.length > 0 &&
      stackableItems.every(function (item) {
        return !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
      });

    log(
      'Reconcile: stackable items =', stackableItems.length,
      'qty =', stackableQty,
      'allSubscription =', allSubscription
    );

    var currentDiscounts = getAppliedDiscounts();
    var hasStackCode = currentDiscounts.indexOf(STACK_CODE) !== -1;
    var hasStackSubCode = currentDiscounts.indexOf(STACK_SUB_CODE) !== -1;

    if (stackableQty >= STACK_THRESHOLD) {
      // Qualifies — apply the right code
      var targetCode = allSubscription ? STACK_SUB_CODE : STACK_CODE;
      var alreadyApplied =
        (targetCode === STACK_CODE && hasStackCode) ||
        (targetCode === STACK_SUB_CODE && hasStackSubCode);

      if (alreadyApplied) {
        // Check if we need to swap (sub changed to one-time or vice versa)
        var wrongCodeApplied =
          (targetCode === STACK_CODE && hasStackSubCode) ||
          (targetCode === STACK_SUB_CODE && hasStackCode);
        if (wrongCodeApplied) {
          return applyDiscount(targetCode);
        }
        return Promise.resolve();
      }
      return applyDiscount(targetCode);
    }

    // Does not qualify — remove any stack codes present
    if (hasStackCode || hasStackSubCode) {
      return removeStackDiscounts().then(function () {
        // Force drawer to re-render so removed discount disappears from UI
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
    }

    return Promise.resolve();
  }

  // ──────── Debounced event handler ────────
  var reconcileTimer = null;
  function handleCartUpdated(event) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      var cart = event && event.detail && event.detail.cart;
      if (cart) {
        reconcile(cart);
      } else {
        fetchCart().then(reconcile).catch(function (err) {
          log('reconcile fetch failed', err);
        });
      }
    }, DEBOUNCE_MS);
  }

  // ──────── Initial reconcile on page load ────────
  function initialReconcile() {
    // Only run if we have stackable IDs and the cart is non-empty
    var stackableIds = getStackableIds();
    if (!stackableIds.length) return;

    fetchCart()
      .then(reconcile)
      .catch(function (err) {
        log('initial reconcile failed', err);
      });
  }

  // ──────── Wire up ────────
  // FoxTheme's CartItems.onCartUpdate dispatches cart:updated after every cart change.
  document.addEventListener('cart:updated', handleCartUpdated);

  // Listen for quiz's custom event so we reconcile before the quiz's redirect happens.
  // The quiz dispatches a cancelable 'zh-sb:added-to-cart' event. We preventDefault to
  // cancel its /cart redirect, then fire cart:refresh ourselves to open the drawer.
  document.addEventListener('zh-sb:added-to-cart', function (event) {
    event.preventDefault(); // cancel quiz's default redirect to /cart
    log('Quiz added to cart — reconciling and opening drawer');

    // Debounced reconcile. Drawer open happens after reconcile settles.
    setTimeout(function () {
      fetchCart()
        .then(reconcile)
        .then(function () {
          // Open drawer via FoxTheme's cart:refresh pattern
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { open: true }
            })
          );
        })
        .catch(function (err) {
          log('Quiz reconcile failed', err);
        });
    }, 100);
  });

  // Run once on page load in case cart arrived in pre-qualifying state (e.g., user
  // added items in another tab, or an old discount code is persisted incorrectly).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialReconcile);
  } else {
    initialReconcile();
  }

  // Expose for debugging / external calls
  window.ZHStackReconciler = {
    reconcile: function () {
      return fetchCart().then(reconcile);
    },
    getStackableIds: getStackableIds,
    getAppliedDiscounts: getAppliedDiscounts,
    applyDiscount: applyDiscount,
    removeStackDiscounts: removeStackDiscounts,
    setDebug: function (val) { DEBUG = !!val; }
  };

  log('Initialized. Stackable product count:', getStackableIds().length);
})();
